import { useTheme } from "@/theme/ThemeProvider";
import { AppButton } from "@/ui/components/AppButton";
import React from "react";
import { ActivityIndicator, Text, View, ViewStyle } from "react-native";

type IconComponent = React.ComponentType<{
  size?: number;
  color?: string;
  weight?: "thin" | "light" | "regular" | "bold" | "fill" | "duotone";
}>;

/**
 * Einheitliche Zustands-Anzeigen für Lade-, Fehler- und Leerzustände.
 * Statt auf jeder Seite eigene Spinner/Fehlerboxen zu bauen.
 */

export function LoadingState({
  message = "Lädt…",
  style,
}: {
  message?: string | null;
  style?: ViewStyle;
}) {
  const { theme } = useTheme();
  return (
    <View
      style={[
        {
          padding: theme.space.lg,
          alignItems: "center",
          justifyContent: "center",
          gap: theme.space.sm,
        },
        style,
      ]}
    >
      <ActivityIndicator color={theme.color.primary} />
      {message ? (
        <Text style={{ color: theme.color.muted }}>{message}</Text>
      ) : null}
    </View>
  );
}

export function ErrorState({
  message,
  onRetry,
  retryLabel = "Erneut versuchen",
  style,
}: {
  message: string;
  onRetry?: () => void;
  retryLabel?: string;
  style?: ViewStyle;
}) {
  const { theme } = useTheme();
  return (
    <View
      style={[
        {
          padding: theme.space.lg,
          borderRadius: theme.radius.lg,
          backgroundColor: theme.color.dangerBg,
          borderWidth: 1,
          borderColor: theme.color.danger,
          gap: theme.space.md,
        },
        style,
      ]}
    >
      <Text style={{ color: theme.color.dangerText, fontWeight: "700", textAlign: "center" }}>
        {message}
      </Text>
      {onRetry ? (
        <AppButton variant="secondary" onPress={onRetry}>
          {retryLabel}
        </AppButton>
      ) : null}
    </View>
  );
}

export function EmptyState({
  title,
  message,
  icon: Icon,
  action,
  style,
}: {
  title?: string;
  message?: string;
  icon?: IconComponent;
  action?: React.ReactNode;
  style?: ViewStyle;
}) {
  const { theme } = useTheme();
  return (
    <View
      style={[
        {
          padding: theme.space.xl,
          alignItems: "center",
          justifyContent: "center",
          gap: theme.space.sm,
        },
        style,
      ]}
    >
      {Icon ? <Icon size={32} color={theme.color.muted} weight="duotone" /> : null}
      {title ? (
        <Text
          style={{
            color: theme.color.text,
            fontWeight: "700",
            fontSize: theme.fontSize.h3,
            textAlign: "center",
          }}
        >
          {title}
        </Text>
      ) : null}
      {message ? (
        <Text style={{ color: theme.color.muted, textAlign: "center" }}>{message}</Text>
      ) : null}
      {action ? <View style={{ marginTop: theme.space.sm }}>{action}</View> : null}
    </View>
  );
}
